/**
 * Organization and school inquiry forms (owner's copy, doc 369-379 and
 * 385-393), and the "Request a call" form. All post to the existing contact
 * endpoint: `payload` fields fill name / email / phone, every other answer
 * is written into `message` under its `messageLabel`. The organization or
 * school name goes in the message, never in `name` (the API rejects
 * link-like names).
 *
 * Field types: text, email, tel, number, textarea, radio, checkbox.
 * Optional per field: `hint` (shown under the input), `placeholder`, and
 * `check(answer)` returning a problem in plain words (or "").
 * Optional per form: `intro`, `submitLabel`, `successText`, `successToast`.
 */

const SHORT = 200;
const LONG = 3000;

export const ORGANIZATION_INQUIRY = {
  id: "organization-inquiry",
  tag: "[Organization inquiry]",
  heading: "Organization Inquiry Form",
  fields: [
    { name: "organization", label: "Organization / Company Name", messageLabel: "Organization", type: "text", required: true, maxLength: SHORT, autoComplete: "organization" },
    { name: "contactName", label: "Contact Name", payload: "name", type: "text", required: true, maxLength: SHORT, autoComplete: "name" },
    { name: "role", label: "Role", messageLabel: "Contact role", type: "text", maxLength: SHORT, autoComplete: "organization-title" },
    { name: "email", label: "Email", payload: "email", type: "email", required: true, maxLength: SHORT, autoComplete: "email" },
    { name: "phone", label: "Phone Number", payload: "phone", type: "tel", maxLength: 32, autoComplete: "tel" },
    {
      name: "eventType",
      label: "Type of event",
      messageLabel: "Type of event",
      type: "radio",
      options: ["Community workshop", "Corporate wellness", "Gallery/cultural event", "Staff wellness day", "Other"],
    },
    { name: "participantGroup", label: "Participant group", messageLabel: "Participant group", type: "radio", options: ["Adults", "Teens", "Children", "Mixed"] },
    { name: "participants", label: "Estimated number of participants", messageLabel: "Estimated participants", type: "number", min: 1 },
    { name: "format", label: "Format", messageLabel: "Format", type: "radio", options: ["In-person", "Virtual"] },
    { name: "dates", label: "Preferred date(s) or timeframe", messageLabel: "Preferred date(s) or timeframe", type: "text", maxLength: SHORT },
    { name: "materials", label: "Will you provide materials?", messageLabel: "Will provide materials", type: "radio", options: ["Yes", "No", "Unsure"] },
    { name: "details", label: "Tell me more about what you have in mind", messageLabel: "Details", type: "textarea", maxLength: LONG },
  ],
};

export const SCHOOL_INQUIRY = {
  id: "school-inquiry",
  tag: "[School inquiry]",
  heading: "School Inquiry Form",
  fields: [
    { name: "school", label: "School Name", messageLabel: "School", type: "text", required: true, maxLength: SHORT, autoComplete: "organization" },
    { name: "contactName", label: "Contact Name", payload: "name", type: "text", required: true, maxLength: SHORT, autoComplete: "name" },
    { name: "role", label: "Role", messageLabel: "Contact role", type: "text", maxLength: SHORT, autoComplete: "organization-title" },
    { name: "email", label: "Email", payload: "email", type: "email", required: true, maxLength: SHORT, autoComplete: "email" },
    { name: "phone", label: "Phone Number", payload: "phone", type: "tel", maxLength: 32, autoComplete: "tel" },
    {
      name: "ageGroups",
      label: "Student age group(s)",
      messageLabel: "Student age group(s)",
      type: "checkbox",
      options: ["Early childhood (4-7)", "Primary (8-11)", "Secondary (12-18)", "Mixed"],
    },
    { name: "students", label: "Estimated number of students", messageLabel: "Estimated students", type: "number", min: 1 },
    // Doc 391 says "aids"; "aides" (support staff) is meant.
    { name: "aides", label: "Does your school have support staff or aides available?", messageLabel: "Support staff or aides available", type: "radio", options: ["Yes", "No", "Unsure"] },
    { name: "startTerm", label: "Preferred start term", messageLabel: "Preferred start term", type: "text", maxLength: SHORT },
    { name: "details", label: "Tell me more about your school community and what you’re hoping for", messageLabel: "Details", type: "textarea", maxLength: LONG },
  ],
};

/** At least 7 digits, so it is a number she can call. */
const checkPhone = (answer) =>
  answer.replace(/\D/g, "").length >= 7 ? "" : "Please enter a phone number I can call (at least 7 digits).";

/**
 * "Request a call" (her November 2026 corrections): replaces the free
 * 15-minute consultation, which is no longer booked online. Lands in her
 * Messages as a contact message that starts with "Call request".
 */
export const REQUEST_CALL = {
  id: "request-call",
  tag: "Call request",
  heading: "Request a call",
  intro:
    "Not sure where to start, or which service is right for you? Leave your number and the times that work for you, and I’ll give you a call.",
  submitLabel: "Request a call",
  successToast: "Thanks! I’ll call you soon.",
  successText: "Thanks! Your request was sent. I’ll call you at one of the times you gave.",
  fields: [
    { name: "name", label: "Full Name", payload: "name", type: "text", required: true, maxLength: SHORT, autoComplete: "name" },
    { name: "email", label: "Email", payload: "email", type: "email", required: true, maxLength: SHORT, autoComplete: "email" },
    { name: "phone", label: "Phone Number", payload: "phone", type: "tel", required: true, maxLength: 32, autoComplete: "tel", check: checkPhone },
    {
      name: "times",
      label: "Times that work for you",
      messageLabel: "Times that work",
      type: "text",
      required: true,
      maxLength: SHORT,
      hint: "For example: weekdays after 4 PM, or Saturday morning.",
    },
    { name: "note", label: "Anything you’d like me to know before I call", messageLabel: "Note", type: "textarea", maxLength: LONG },
  ],
};

/** Blank values for a form definition (checkbox groups start as []). */
export function emptyInquiryValues(form) {
  const values = { website: "" }; // honeypot
  form.fields.forEach((f) => {
    values[f.name] = f.type === "checkbox" ? [] : "";
  });
  return values;
}

const answerText = (value) => (Array.isArray(value) ? value.join(", ") : String(value ?? "").trim());

/**
 * Write the non-payload answers into the contact `message`:
 *
 *   [Organization inquiry]
 *   Organization: Acme Ltd
 *   Type of event: Staff wellness day
 *   Details:
 *   free text...
 *
 * Unanswered questions are left out. Long-text answers go on their own lines.
 */
export function composeInquiryMessage(form, values) {
  const lines = [form.tag];
  form.fields.forEach((f) => {
    if (f.payload) return;
    const answer = answerText(values[f.name]);
    if (!answer) return;
    if (f.type === "textarea") {
      lines.push(`${f.messageLabel}:`, answer);
    } else {
      lines.push(`${f.messageLabel}: ${answer}`);
    }
  });
  return lines.join("\n");
}

/** The contact-endpoint body for sendContactForm. */
export function buildInquiryContact(form, values) {
  const byPayload = (key) => {
    const field = form.fields.find((f) => f.payload === key);
    return field ? answerText(values[field.name]) : "";
  };
  return {
    name: byPayload("name"),
    email: byPayload("email"),
    phone: byPayload("phone"),
    message: composeInquiryMessage(form, values),
    website: values.website || "",
  };
}

/** Labels of required fields left blank. */
export function missingRequired(form, values) {
  return form.fields.filter((f) => f.required && !answerText(values[f.name])).map((f) => f.label);
}

/** The first problem a field's own `check` finds in a given answer, or "". */
export function firstInvalidAnswer(form, values) {
  for (const f of form.fields) {
    const answer = answerText(values[f.name]);
    const problem = f.check && answer ? f.check(answer) : "";
    if (problem) return problem;
  }
  return "";
}
